import axios from "axios";

import {
  ElectricalPanelTypes,
  LightingTypes,
  SceneDetail,
} from "./automationTypes";
import { LightDetailItem } from "components/forms/LightDetail";
import { RefrigerationDetailItem } from "components/forms/RefrigerationDetail";
import { ElectricalPanelDetailItem } from "components/forms/ElectricalPanelDetail";

export const fetchSceneDetails = async (
  token: string,
  spaceId: string,
  sceneId: string
): Promise<SceneDetail> => {
  return axios
    .get(
      `${process.env.REACT_APP_API_PATH}/automation/${spaceId}/scene${
        sceneId ? `?sceneId=${sceneId}` : ""
      }`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    )
    .then(({ data }) => {
      return data;
    });
};

export const fetchInstallations = async (
  token: string
): Promise<SceneDetail> => {
  return axios
    .get(`${process.env.REACT_APP_API_PATH}/energy_operators/credential`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
    .then(({ data }) => {
      console.log("hi", data);
      return data;
    });
};

export const createOrUpdateScene = async (
  token: string,
  data
): Promise<SceneDetail> => {
  return axios
    .put(`${process.env.REACT_APP_API_PATH}/automation/scene`, data, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
    .then(({ data }) => {
      return data;
    });
};

export const fetchPanelTypes = async (
  token: string
): Promise<ElectricalPanelTypes[]> => {
  return axios
    .get(`${process.env.REACT_APP_API_PATH}/users/electrical-panel-types`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
    .then(({ data }) => {
      return data.data;
    });
};

export const createElectricPanel = async (
  token: string,
  environmentId,
  panelsData: ElectricalPanelDetailItem[]
) => {
  const formattedPanels = panelsData.map((panel) => ({
    room_id: environmentId,
    electric_panel: panel.panelName,
    panel_type: panel.panelTypeId,
    voltage_nominal: panel.tension,
    current_nominal: panel.current,
    conservation_state: panel.conservationStatus,
    nr10: {
      panel_identification: panel.nr10.panelIdentification,
      load_identification_in_devices: panel.nr10.devicesIdentification,
      accidental_contact_protection: panel.nr10.accidentalContactProtection,
      surge_protection_device: panel.nr10.surgeProtectionDevice,
      grounding_equipotentialization: panel.nr10.groundingAndEquipotential,
      busbars_conductor_separation: panel.nr10.neutralEarthSeparation,
      adequate_cable_section: panel.nr10.cableSectionAdequacy,
      lockout_application: panel.nr10.workLockout,
      warning_signs: panel.nr10.warningPlates,
      electrical_diagrams: panel.nr10.electricalDiagrams,
      cable_entry_sealing: panel.nr10.cableEntrySealing,
      cable_organization: panel.nr10.cableOrganization,
      sufficient_internal_space: panel.nr10.internalSpaceAdequacy,
    },
    observations: panel.obs,
    photos: panel.photos,
  }));
  return axios
    .post(
      `${process.env.REACT_APP_API_PATH}/electric-panel`,
      {
        electric_panels: formattedPanels,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      }
    )
    .then(({ data }) => {
      return data;
    });
};

export const fetchLightingOptions = async (
  token: string
): Promise<LightingTypes[]> => {
  return axios
    .get(
      `${process.env.REACT_APP_API_PATH}/building-structure/lighting-types`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    )
    .then(({ data }) => {
      return data.data;
    });
};

export const createLighting = async (
  token: string,
  environmentId: string,
  lightsData: LightDetailItem[]
) => {
  const formattedLights = lightsData.map((light) => ({
    activation_type: light.activation_type || null,
    average_usage_time: light.usage_time,
    ceiling_type: light.ceiling_type || null,
    conservation_state: light.conservation_state || null,
    environment_id: environmentId,
    lighting_type: light.type,
    notes: light.obs || null,
    pictures: light.photos || [],
    quantity: light.amount,
    power: light.power,
  }));
  return axios
    .post(
      `${process.env.REACT_APP_API_PATH}/building-structure/lighting`,
      {
        lightings: formattedLights,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      }
    )
    .then(({ data }) => {
      return data;
    });
};

export const createRefrigeration = async (
  token: string,
  environmentId: string,
  refrigerationData: RefrigerationDetailItem[]
) => {
  const formattedRefrigeration = refrigerationData.map((refrigeration) => ({
    capacity: refrigeration.capacity || null,
    conservation_state: refrigeration.conservationStatus || null,
    description: refrigeration.obs || null,
    environment_id: environmentId,
    equipment_type: refrigeration.equipmentType || null,
    images: refrigeration.pictures || [],
    nominal_power: refrigeration.nominalPower || null,
    quantity: refrigeration.quantity || null,
    system_type: refrigeration.systemType || null,
  }));
  return axios
    .post(
      `${process.env.REACT_APP_API_PATH}/cooling-system`,
      {
        cooling_system: formattedRefrigeration,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      }
    )
    .then(({ data }) => {
      return data;
    });
};

export const createEngines = async (
  token: string,
  environmentId: string,
  enginesData: any[]
) => {
  const formatted = enginesData.map((engine) => ({
    system_type: engine.engineSystemType || null,
    motor_power_hp: engine.nominalPower || null,
    quantity: engine.quantity || null,
    frequency_inverter: engine.frequencyInverter || null,
    soft_starter: engine.softStarter || null,
    average_load_pct: engine.averageLoad || null,
    nominal_efficiency_pct: engine.nominalEfficiency || null,
    power_factor: engine.powerFactor || null,
    efficiency_at_load_point: engine.efficiencyAtLoadPoint || null,
    age: engine.age || null,
    conservation_state: engine.conservationStatus || null,
    observations: engine.obs || null,
    environment_id: environmentId,
    images: engine.pictures || [],
  }));

  return axios
    .post(
      `${process.env.REACT_APP_API_PATH}/motors-and-drives`,
      {
        motors_and_drives: formatted,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      }
    )
    .then(({ data }) => {
      return data;
    });
};

export const createHeatingWater = async (
  token: string,
  environmentId: string,
  heatingWaterData: any[]
) => {
  const formatted = heatingWaterData.map((item) => ({
    auxiliary_heating_power_w: item.auxiliaryHeatingPowerW || null,
    average_monthly_energy_kwh: item.averageMonthlyEnergyKwh || null,
    baths_avg_time_min: item.bathsAvgTimeMin || null,
    baths_quantity_per_day: item.bathsQuantityPerDay || null,
    collector_area_per_uc_m2: item.collectorAreaPerUcM2 || null,
    efficiency_pct: item.efficiencyPct || null,
    environment_id: environmentId,
    external_area_m2: item.externalAreaM2 || null,
    max_shower_power_w: item.maxShowerPowerW || null,
    peak_coincidence_factor: item.peakCoincidenceFactor || null,
    reservoir_volume_l: item.reservoirVolumeL || null,
    shower_flow_l_min: item.showerFlowLMin || null,
    showers_per_uc_quantity: item.showersPerUcQuantity || null,
    solar_collector_model: item.solarCollectorModel || null,
    solar_collectors_quantity: item.solarCollectorsQuantity || null,
    system_type: item.systemType || null,
    thermal_reservoir_model: item.thermalReservoirModel || null,
    // fields missing in backend
    conservation_state: item.conservationStatus || null,
    observations: item.obs || null,
    images: item.images || [],
  }));

  return axios
    .post(
      `${process.env.REACT_APP_API_PATH}/heating-water`,
      {
        heating_water: formatted,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      }
    )
    .then(({ data }) => {
      return data;
    });
};

export const createOtherCargo = async (
  token: string,
  environmentId: string,
  cargoData: any[]
) => {
  const formatted = cargoData.map((item) => ({
    environment_id: environmentId,
    system_description: item.systemDescription || null,
    technology_type: item.technologyType || null,
    reted_power: item.ratedPower || null,
    installed_capacity: item.installedCapacity || null,
    conservation_state: item.conservationStatus || null,
    observations: item.obs || null,
    images: item.images || [],
  }));

  return axios
    .post(
      `${process.env.REACT_APP_API_PATH}/load-registration`,
      {
        load_registration: formatted,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      }
    )
    .then(({ data }) => {
      return data;
    });
};
